import React, { useEffect, useState } from 'react';
import { Toast as ToastType } from '../hooks/useToast';

interface ToastItemProps {
  toast: ToastType;
  onRemove: (id: number) => void;
}

/**
 * 個別のトースト通知コンポーネント
 */
const ToastItem = React.memo<ToastItemProps>(({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // エントランスアニメーション
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 200);
  };

  const getIcon = (): string => {
    switch (toast.type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
      default:
        return 'ℹ️';
    }
  };

  const getClassName = (): string => {
    let className = `toast toast-${toast.type}`;
    if (isVisible) className += ' toast-visible';
    if (isExiting) className += ' toast-exiting';
    return className;
  };

  return (
    <div className={getClassName()}>
      <div className="toast-content">
        <div className="toast-header">
          <span className="toast-icon" role="img" aria-label={toast.type}>
            {getIcon()}
          </span>
          <span className="toast-message">{toast.message}</span>
          <button className="toast-close" onClick={handleClose} aria-label="通知を閉じる">
            ×
          </button>
        </div>

        {toast.guidance && (
          <div className="toast-guidance">
            <span className="toast-guidance-icon">💡</span>
            <span className="toast-guidance-text">{toast.guidance}</span>
          </div>
        )}
      </div>
    </div>
  );
});

ToastItem.displayName = 'ToastItem';

interface ToastProps {
  toasts: ToastType[];
  onRemove: (id: number) => void;
}

/**
 * トースト通知コンテナコンポーネント
 * 画面右上に複数のトーストを縦に並べて表示
 */
const Toast = React.memo<ToastProps>(({ toasts, onRemove }) => {
  if (!toasts || toasts.length === 0) {
    return null;
  }

  return (
    <div className="toast-container" role="alert" aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
});

Toast.displayName = 'Toast';

export default Toast;
